import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { CopilotActionsItemState, CopilotActionsView } from '../../models/copilot-actions.model';
import { SocketService } from '../socket/socket.service';
import { AttendanceCopilotApiService } from './attendance-copilot-api.service';
import { CopilotSocketPayload } from './attendance-copilot-socket.service';

export interface CopilotActionsUiState {
  idAttendance: string | null;
  view: CopilotActionsView | null;
  /** A generation this screen asked for is on its way to the API. */
  requesting: boolean;
  /** The actions whose marks are on their way to the API. */
  marking: string[];
}

const INITIAL_STATE: CopilotActionsUiState = {
  idAttendance: null,
  view: null,
  requesting: false,
  marking: [],
};

const ACTIONS_SIGNAL_EVENT = 'copilot:actions';

/**
 * The actions of the open conversation, above the tabs (SPEC-2026-10-copilot-attendance-actions,
 * plan AD-10): one GET when the conversation opens, one more on every `copilot:actions` signal,
 * and no polling. It shares the room `copilot_attendance_{id}` with
 * `AttendanceCopilotInsightService`, whose shape it follows, so both are opened and closed together.
 */
@Injectable({ providedIn: 'root' })
export class AttendanceCopilotActionsService {
  private readonly state$ = new BehaviorSubject<CopilotActionsUiState>({ ...INITIAL_STATE });
  private signals: Subscription | null = null;
  private room: string | null = null;

  constructor(
    private readonly socketService: SocketService,
    private readonly apiService: AttendanceCopilotApiService,
  ) { }

  get snapshot(): CopilotActionsUiState {
    return this.state$.getValue();
  }

  select(): Observable<CopilotActionsUiState> {
    return this.state$.asObservable();
  }

  /** Follows a conversation: joins its Copilot room and reads its actions. */
  open(idAttendance: string): void {
    if (this.snapshot.idAttendance === idAttendance) return;
    this.close();
    this.state$.next({ ...INITIAL_STATE, idAttendance });
    this.room = `copilot_attendance_${idAttendance}`;
    this.socketService.joinEvent(this.room);
    // A signal carries no content (plan AD-06): the actions are read again from the API — only on
    // this conversation's signals, since the socket stays in every room it joined.
    this.signals = this.socketService
      .listen(ACTIONS_SIGNAL_EVENT)
      .subscribe((payload: CopilotSocketPayload) => {
        if (payload?.conversationId === this.snapshot.idAttendance) this.refresh();
      });
    this.fetch(idAttendance);
  }

  /** Stops following the conversation. */
  close(): void {
    this.signals?.unsubscribe();
    this.signals = null;
    if (this.room) this.socketService.leaveEvent(this.room);
    this.room = null;
    this.state$.next({ ...INITIAL_STATE });
  }

  /** Reads the actions again, as after a transfer changes who may mark them (RF-32). */
  refresh(): void {
    const { idAttendance } = this.snapshot;
    if (idAttendance) this.fetch(idAttendance);
  }

  /** Marks one action; a refused mark reads the actions again, so the screen shows the current ones (RF-28). */
  mark(itemId: string, state: CopilotActionsItemState): void {
    const { idAttendance, marking } = this.snapshot;
    if (!idAttendance || marking.includes(itemId)) return;
    this.patch({ marking: [...marking, itemId] });
    this.apiService.markAction(idAttendance, itemId, state).subscribe({
      next: (res) => this.settle(idAttendance, { view: res.data }, itemId),
      error: () => {
        this.settle(idAttendance, {}, itemId);
        this.fetch(idAttendance);
      },
    });
  }

  /** Retries a failed generation, or asks for the first one; the API refuses what the screen should not offer (RF-09, spec M-2). */
  generate(): void {
    const { idAttendance, requesting } = this.snapshot;
    if (!idAttendance || requesting) return;
    this.patch({ requesting: true });
    this.apiService.generateActions(idAttendance).subscribe({
      next: (res) => this.settle(idAttendance, { view: res.data }),
      error: () => this.fetch(idAttendance),
    });
  }

  private fetch(idAttendance: string): void {
    this.apiService.getActions(idAttendance).subscribe({
      next: (res) => this.settle(idAttendance, { view: res.data }),
      error: () => this.settle(idAttendance, {}),
    });
  }

  // Every answer ends the request it belongs to; one of a conversation no longer open is dropped.
  private settle(idAttendance: string, partial: Partial<CopilotActionsUiState>, markedId?: string): void {
    if (this.snapshot.idAttendance !== idAttendance) return;
    const marking = this.snapshot.marking.filter((id) => id !== markedId);
    this.patch({ ...partial, requesting: false, marking });
  }

  private patch(partial: Partial<CopilotActionsUiState>): void {
    this.state$.next({ ...this.snapshot, ...partial });
  }
}
